import React, { useState, useEffect } from "react";
import { Table } from "antd";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend } from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

const currency = localStorage.getItem("currencycode") || "₹";

const MealPlanSummary = ({ dateRange, setExportData, setExportHeaders, setExportFooter, setTotalsForPageFooter, setTotalColumns, setReportTitle }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [mealPlanData, setMealPlanData] = useState([]);
  const [applicableTaxes, setApplicableTaxes] = useState([]);
  const apiURL = process.env.REACT_APP_API_URL || "http://localhost:4000";

  useEffect(() => {
    const fetchMealPlanSummaryData = async () => {
      if (!dateRange || !dateRange.startDate || !dateRange.endDate) {
        console.error("dateRange is undefined or missing startDate/endDate:", dateRange);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("No authentication token found");
        }

        const response = await axios.get(`${apiURL}/reports-daily/meal-plan-wise-summary`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            startDate: dateRange.startDate.format("YYYY-MM-DD"),
            endDate: dateRange.endDate.format("YYYY-MM-DD"),
            page: 1,
            pageSize: 100,
          },
        });
        if (response.data.success) {
          setMealPlanData(response.data.report || []);
          setApplicableTaxes(response.data.applicableTaxes || []);
        } else {
          throw new Error(response.data.message || "Failed to fetch Meal Plan Summary Report");
        }
      } catch (error) {
        console.error("Error fetching Meal Plan Summary Report:", error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMealPlanSummaryData();
  }, [dateRange, apiURL]);

  const columns = [
    {
      title: t("meal_plan"),
      dataIndex: "meal_plan",
      key: "meal_plan",
      sorter: (a, b) => a.meal_plan.localeCompare(b.meal_plan),
    },
    {
      title: t("room_sold_count"),
      dataIndex: "room_sold_count",
      key: "room_sold_count",
      sorter: (a, b) => a.room_sold_count - b.room_sold_count,
    },
    {
      title: t("total_nights"),
      dataIndex: "total_nights",
      key: "total_nights",
      sorter: (a, b) => a.total_nights - b.total_nights,
    },
    {
      title: t("actual_price"),
      dataIndex: "actual_price",
      key: "actual_price",
      render: (text) => `${currency}${Number(text || 0).toFixed(2)}`,
      sorter: (a, b) => a.actual_price - b.actual_price,
    },
    {
      title: t("commission_discount"),
      dataIndex: "discount",
      key: "discount",
      render: (text) => `${currency}${Number(text || 0).toFixed(2)}`,
      sorter: (a, b) => a.discount - b.discount,
    },
    ...applicableTaxes.map((taxName) => ({
      title: `${taxName} (${currency})`,
      dataIndex: taxName,
      key: taxName,
      render: (text) => `${currency}${Number(text || 0).toFixed(2)}`,
      sorter: (a, b) => (a[taxName] || 0) - (b[taxName] || 0),
    })),
    {
      title: t("total_tax"),
      dataIndex: "total_tax",
      key: "total_tax",
      render: (text) => `${currency}${Number(text || 0).toFixed(2)}`,
      sorter: (a, b) => a.total_tax - b.total_tax,
    },
    {
      title: t("net_revenue"),
      dataIndex: "net_revenue",
      key: "net_revenue",
      render: (text) => `${currency}${Number(text || 0).toFixed(2)}`,
      sorter: (a, b) => a.net_revenue - b.net_revenue,
      onCell: () => ({ style: { backgroundColor: "#e2e6ea" } }),
    },
  ];

  const totals = mealPlanData.reduce(
    (acc, item) => ({
      room_sold_count: acc.room_sold_count + (item.room_sold_count || 0),
      total_nights: acc.total_nights + (item.total_nights || 0),
      actual_price: acc.actual_price + (item.actual_price || 0),
      discount: acc.discount + (item.discount || 0),
      total_tax: acc.total_tax + (item.total_tax || 0),
      net_revenue: acc.net_revenue + (item.net_revenue || 0),
      ...applicableTaxes.reduce((taxAcc, taxName) => ({
        ...taxAcc,
        [taxName]: (taxAcc[taxName] || 0) + (item[taxName] || 0),
      }), acc),
    }),
    {
      room_sold_count: 0,
      total_nights: 0,
      actual_price: 0,
      discount: 0,
      total_tax: 0,
      net_revenue: 0,
      ...applicableTaxes.reduce((acc, taxName) => ({ ...acc, [taxName]: 0 }), {}),
    }
  );

  // Chart data for net_revenue (bar) and room_sold_count (line)
  const barChartData = {
    labels: mealPlanData.map((item) => item.meal_plan || "N/A"),
    datasets: [
      {
        type: "bar",
        label: t("net_revenue"),
        data: mealPlanData.map((item) => item.net_revenue || 0),
        backgroundColor: "#28a745",
        yAxisID: "y",
      },
      {
        type: "line",
        label: t("room_sold_count_trend"),
        data: mealPlanData.map((item) => item.room_sold_count || 0),
        borderColor: "#ff6347",
        backgroundColor: "#ff6347",
        fill: false,
        tension: 0.4,
        yAxisID: "y1",
      },
    ],
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        type: "linear",
        display: true,
        position: "left",
        title: { display: true, text: `${t("net_revenue")} (${currency})` },
        beginAtZero: true,
      },
      y1: {
        type: "linear",
        display: true,
        position: "right",
        title: { display: true, text: t("room_sold_count") },
        beginAtZero: true,
        grid: { drawOnChartArea: false },
      },
      x: {
        title: { display: true, text: t("meal_plan") },
      },
    },
    plugins: {
      legend: { position: "top" },
      title: { display: true, text: t("meal_plan_summary_chart") },
    },
  };

  useEffect(() => {
    if (mealPlanData.length > 0) {
      setExportData(mealPlanData);
      setExportHeaders(columns.map((col) => ({ key: col.key, label: col.title })));
      setExportFooter([
        { key: "room_sold_count", value: totals.room_sold_count, currency: false },
        { key: "total_nights", value: totals.total_nights, currency: false },
        { key: "actual_price", value: totals.actual_price, currency: true },
        { key: "discount", value: totals.discount, currency: true },
        ...applicableTaxes.map((taxName) => ({
          key: taxName,
          value: totals[taxName],
          currency: true,
        })),
        { key: "total_tax", value: totals.total_tax, currency: true },
        { key: "net_revenue", value: totals.net_revenue, currency: true },
      ]);
      setTotalsForPageFooter([{ key: t("total_bookings"), value: totals.room_sold_count }]);
      setTotalColumns(["room_sold_count", "total_nights", "actual_price", "discount", ...applicableTaxes, "total_tax", "net_revenue"]);
      setReportTitle(t("meal_plan_summary"));
    }
  }, [mealPlanData, applicableTaxes, setExportData, setExportHeaders, setExportFooter, setTotalsForPageFooter, setTotalColumns, setReportTitle, t]);

  return (
    <div className="meal-plan-summary-container">
      {loading ? (
        <div className="text-center my-5">
          <div className="loader">
            <div className="dot"></div>
            <div className="dot"></div>
            <div className="dot"></div>
            <div className="dot"></div>
            <div className="dot"></div>
          </div>
        </div>
      ) : mealPlanData.length === 0 ? (
        <div className="no-data-container text-center mt-4" style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
          <img
            src="https://ui-cdn.digitalocean.com/apps/ui-spaces/static/media/spaces-empty.5e1d7947c43b7e336b14e421d4a86639.svg"
            alt="No data available"
            style={{ width: "150px", marginBottom: "20px" }}
          />
          <h3>{t("no_data")}</h3>
          <p>{t("no_meal_plan_summary_data")}</p>
        </div>
      ) : (
        <>
          <div className="row mb-4">
            <div className="col-md-12">
              <div className="card" style={{ borderRadius: "0", overflowX: "auto", border: "1px solid #dee2e6", borderBottom: "none" }}>
                <div className="card-header" style={{ backgroundColor: "#fff", padding: "12px 16px", borderBottom: "none", position: "relative" }}>
                  <h5 className="mb-0 d-flex align-items-center" style={{ fontSize: "14px", fontWeight: 600, color: "#333", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    {t("meal_plan_summary_chart")} (Total Bookings: {totals.room_sold_count})
                  </h5>
                  <div style={{ position: "absolute", bottom: "0", left: "0", width: "100%", height: "4px", backgroundColor: "#28a745" }} />
                </div>
                <div className="card-body" style={{ padding: "10px", height: "300px" }}>
                  <div style={{ height: "100%", width: "100%" }}>
                    <Bar data={barChartData} options={barChartOptions} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row g-0">
            <div className="col-md-12">
              <div className="card" style={{ borderRadius: "0", overflowX: "auto", border: "1px solid #dee2e6", borderBottom: "none" }}>
                <div className="card-header" style={{ backgroundColor: "#fff", padding: "12px 16px", borderBottom: "none", position: "relative" }}>
                  <h5 className="mb-0 d-flex align-items-center" style={{ fontSize: "14px", fontWeight: 600, color: "#333", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    {t("meal_plan_summary")} (Total Bookings: {totals.room_sold_count})
                  </h5>
                  <div style={{ position: "absolute", bottom: "0", left: "0", width: "100%", height: "4px", backgroundColor: "#007bff" }} />
                </div>
                <div className="card-body p-0">
                  <Table
                    dataSource={mealPlanData}
                    columns={columns}
                    rowKey="meal_plan"
                    pagination={false}
                    bordered
                    className="daybook-table"
                    style={{ margin: 0 }}
                    summary={() => (
                      <Table.Summary.Row style={{ backgroundColor: "#e6f4ea", fontWeight: "700", fontSize: "15px" }}>
                        <Table.Summary.Cell>{t("total")}</Table.Summary.Cell>
                        <Table.Summary.Cell>{totals.room_sold_count}</Table.Summary.Cell>
                        <Table.Summary.Cell>{totals.total_nights}</Table.Summary.Cell>
                        <Table.Summary.Cell>{currency}{totals.actual_price.toFixed(2)}</Table.Summary.Cell>
                        <Table.Summary.Cell>{currency}{totals.discount.toFixed(2)}</Table.Summary.Cell>
                        {applicableTaxes.map((taxName, index) => (
                          <Table.Summary.Cell key={index}>{currency}{totals[taxName].toFixed(2)}</Table.Summary.Cell>
                        ))}
                        <Table.Summary.Cell>{currency}{totals.total_tax.toFixed(2)}</Table.Summary.Cell>
                        <Table.Summary.Cell style={{ backgroundColor: "#e2e6ea" }}>{currency}{totals.net_revenue.toFixed(2)}</Table.Summary.Cell>
                      </Table.Summary.Row>
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      <style jsx>{`
        .table th, .table td {
          padding: 8px 12px;
          vertical-align: middle;
          border: 1px solid #ced4da;
        }
        .daybook-table .ant-table-thead > tr > th {
          background: #ffffff;
          font-weight: 600;
          padding: 8px 16px;
          font-size: 14px;
        }
        .daybook-table .ant-table-tbody > tr > td {
          padding: 12px 18px;
          font-size: 14px;
          height: 60px;
        }
        .daybook-table .ant-table {
          border: 1px solid #dee2e6;
        }
        .daybook-table .ant-table-tbody > tr.ant-table-row:hover > td {
          background: #f5f5f5;
        }
        .chart-container {
          max-height: 300px;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default MealPlanSummary;